"use client";

import Link from "next/link";
import { useId, useState } from "react";

import checkAddToCart, { AddToCartStatus } from "@/app/ec/product/[basePath]/checkAddToCart";
import getChosenPriceBreakExpiration from "@/app/ec/product/[basePath]/vendor-options/getChosenPriceBreakExpiration";
import { useShowLicenseRequiredBadge } from "@/app/hooks/useLicenseRequiredBadge";
import getVendorNameForURL from "@/app/vendor/utils/getVendorNameForURL";
import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import Badge from "@/components/design-system/badge";
import Button from "@/components/design-system/button";
import ExpiresBadge from "@/components/expires-badge/expiresBadge";
import LicenseRequiredBadge from "@/components/license-required-badge/licenseRequiredBadge";
import PurchasedFromBeforeBadge from "@/components/purchased-from-before-badge/purchasedFromBeforeBadge";
import type { VendorPurchase } from "@/store/sellerOptionsSlice";
import { licenseIsRequired } from "@/utils/licenseRequired";
import CaretDownIcon from "public/next-public/common/icons/caret-regular-down.svg";
import StarIcon from "public/next-public/common/icons/star-solid-icon.svg";
import formatPrice from "../pricing/formatPrice";
import getShippingSummary from "../pricing/getShippingSummary";
import ShippingCost, { UnknownHeavyShippingNote } from "../pricing/shippingCost";
import StylizedPrice from "../pricing/stylizedPrice";
import ShippingEstimate from "./shippingEstimate";
import styles from "./sellerOptionRow.module.scss";

/**
 * One offer. In the table the columns are seller, price + shipping, total, and add to cart; the total column
 * drops out below xl. The drawer stacks the same parts for a narrow screen.
 */
export default function SellerOptionRow({
  offer,
  layout,
  quantity,
  isRecommended,
  isLowestPrice,
  purchase,
  productRequiresLicense,
  isAddingToCart,
  addToCartFailed,
  isUpdating,
  onAddToCart,
}: SellerOptionRowProps) {
  const offerBadges = <OfferBadges isRecommended={isRecommended} isLowestPrice={isLowestPrice} />;
  const seller = <SellerName offer={offer} />;
  const details = (
    <div className={styles.details}>
      <span className={styles.estimate}>
        <ShippingEstimate vendorOption={offer} showDeliveryDate={false} />
      </span>
      <SellerBadges offer={offer} purchase={purchase} productRequiresLicense={productRequiresLicense} />
    </div>
  );
  const pricing = <Pricing offer={offer} quantity={quantity} />;
  const addToCart = (
    <AddToCart
      offer={offer}
      quantity={quantity}
      isAddingToCart={isAddingToCart}
      addToCartFailed={addToCartFailed}
      isUpdating={isUpdating}
      onAddToCart={onAddToCart}
    />
  );

  if (layout === "drawer") {
    return (
      <div className={`${styles.SellerOptionRow} ${styles.drawer}`}>
        <div className={styles.drawerHeader}>
          {seller}
          <div className={styles.total}>
            <Total offer={offer} showLabel />
            {offerBadges}
          </div>
        </div>
        {details}
        {pricing}
        {addToCart}
      </div>
    );
  }

  return (
    <div className={`${styles.SellerOptionRow} ${styles.table}`}>
      <div className={styles.seller}>
        {seller}
        {details}
      </div>
      <div className={styles.pricingColumn}>
        <div className={styles.offerBadgesAbovePrice}>{offerBadges}</div>
        {pricing}
      </div>
      <div className={`${styles.total} ${styles.totalColumn}`}>
        <Total offer={offer} />
        {offerBadges}
      </div>
      {addToCart}
    </div>
  );
}

function SellerName({ offer }: { offer: VendorOptionState }) {
  const hasRating = offer.vdrNumberOfGeneralRatings > 0;

  return (
    <div className={styles.sellerName}>
      <Link
        href={`/vendor/${getVendorNameForURL(offer.vendorName)}/${offer.vendorId}`}
        prefetch={false}
        className={styles.vendorName}
        data-seller-option-vendor-link={offer.vendorId}
      >
        {offer.vendorName}
      </Link>
      {hasRating && (
        <span className={styles.rating}>
          <span className={styles.srOnly}>
            Rated {offer.vendorRating.toFixed(1)} out of 5 from {offer.vdrNumberOfGeneralRatings}{" "}
            {offer.vdrNumberOfGeneralRatings === 1 ? "review" : "reviews"}
          </span>
          <span className={styles.ratingValue} aria-hidden>
            <StarIcon className={styles.star} width={16} height={16} />
            {offer.vendorRating.toFixed(1)}
          </span>
          <span className={styles.ratingCount} aria-hidden>
            ({offer.vdrNumberOfGeneralRatings.toLocaleString("en-US")})
          </span>
        </span>
      )}
    </div>
  );
}

function SellerBadges({
  offer,
  purchase,
  productRequiresLicense,
}: Pick<SellerOptionRowProps, "offer" | "purchase" | "productRequiresLicense">) {
  const requiresLicense = licenseIsRequired(offer, productRequiresLicense);
  // The license badge hides itself for a licensed shopper, so the row is gated on the same rule.
  const showLicense = useShowLicenseRequiredBadge(requiresLicense);
  const expiration = getChosenPriceBreakExpiration(offer);

  if (!offer.isAuthorizedDistributor && purchase === undefined && expiration === null && !showLicense) {
    return null;
  }

  return (
    <div className={styles.badges}>
      {offer.isAuthorizedDistributor && <AuthorizedDistributorBadge label="Authorized Seller" />}
      {purchase && <PurchasedFromBeforeBadge vendorName={offer.vendorName} purchase={purchase} />}
      {expiration !== null && <ExpiresBadge expiration={expiration} />}
      <LicenseRequiredBadge licenseIsRequired={requiresLicense} />
    </div>
  );
}

function OfferBadges({ isRecommended, isLowestPrice }: Pick<SellerOptionRowProps, "isRecommended" | "isLowestPrice">) {
  if (!isRecommended && !isLowestPrice) {
    return null;
  }

  return (
    <div className={styles.offerBadges}>
      {isRecommended && <Badge label="Recommended" color="yellow" strong={false} showIcon={false} />}
      {isLowestPrice && <Badge label="Lowest Price" color="magenta" strong={false} showIcon={false} />}
    </div>
  );
}

function Pricing({ offer, quantity }: { offer: VendorOptionState; quantity: number }) {
  const shipping = getShippingSummary(offer, null);

  return (
    <div className={styles.pricing}>
      <div className={styles.priceAndShipping}>
        <span className={styles.price}>
          {formatPrice(offer.chosenPriceBreakUnitPrice)}
          {quantity > 1 && <span className={styles.each}> each</span>}
        </span>
        <ShippingCost shipping={shipping} vendorName={offer.vendorName} />
      </div>
      {shipping.heavyShippingUnknown && <UnknownHeavyShippingNote />}
      {shipping.spendForFreeShipping !== null && (
        <p className={styles.spendMore}>
          Spend <strong>{formatPrice(shipping.spendForFreeShipping)}</strong> more with {offer.vendorName} to get{" "}
          <span className={styles.free}>FREE</span> shipping
        </p>
      )}
      <BulkPricing offer={offer} />
    </div>
  );
}

// Only offers with more than one price break have bulk pricing to show.
function BulkPricing({ offer }: { offer: VendorOptionState }) {
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const priceBreaks = [...offer.priceBreaks].sort((a, b) => a.minQty - b.minQty);

  if (priceBreaks.length < 2) {
    return null;
  }

  return (
    <div className={styles.bulkPricing}>
      <button
        type="button"
        className={styles.bulkPricingToggle}
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={() => setExpanded(!expanded)}
        data-seller-option-bulk-pricing={expanded ? "hide" : "show"}
      >
        {expanded ? "Hide" : "Show"} Bulk Pricing Discounts
        <CaretDownIcon
          className={`${styles.caret} ${expanded ? styles.caretUp : ""}`}
          width={12}
          height={12}
          aria-hidden
        />
      </button>
      <p id={listId} className={styles.priceBreaks} hidden={!expanded}>
        {priceBreaks.map((priceBreak) => `${priceBreak.minQty} at ${formatPrice(priceBreak.unitPrice)}`).join(", ")}
      </p>
    </div>
  );
}

function Total({ offer, showLabel = false }: { offer: VendorOptionState; showLabel?: boolean }) {
  return (
    <div className={styles.totalPrice}>
      {showLabel && (
        <span className={styles.totalLabel} aria-hidden>
          Total:
        </span>
      )}
      <span className={styles.srOnly}>Total {formatPrice(offer.productGrandTotal)}</span>
      <StylizedPrice price={offer.productGrandTotal} size="lg" />
    </div>
  );
}

function AddToCart({
  offer,
  quantity,
  isAddingToCart,
  addToCartFailed,
  isUpdating,
  onAddToCart,
}: Pick<
  SellerOptionRowProps,
  "offer" | "quantity" | "isAddingToCart" | "addToCartFailed" | "isUpdating" | "onAddToCart"
>) {
  const requirementId = useId();
  const errorId = useId();
  const status = checkAddToCart(quantity, offer.minimumQuantityPurchasable, offer.inventory);
  const quantityAllowed = status === AddToCartStatus.OK;
  const describedBy = [quantityAllowed ? null : requirementId, addToCartFailed ? errorId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.addToCart}>
      <Button
        variant="secondary"
        color="blue"
        text="Add to Cart"
        showLeftIcon={false}
        showRightIcon={false}
        className={styles.addToCartButton}
        loading={isAddingToCart}
        disabled={!quantityAllowed || isAddingToCart || isUpdating}
        ariaLabel={`Add to Cart from ${offer.vendorName}`}
        ariaDescribedBy={describedBy || undefined}
        onClick={() => onAddToCart(offer)}
        data-seller-option-add-to-cart={offer.vendorProductId}
      />
      {!quantityAllowed && (
        <span id={requirementId} className={styles.quantityRequirement}>
          {status === AddToCartStatus.MINIMUM_QUANTITY ? (
            <>
              Min qty: {offer.minimumQuantityPurchasable}
              <span className={styles.srOnly}>. Choose a larger quantity or a different seller.</span>
            </>
          ) : (
            <>
              Max qty: {offer.inventory}
              <span className={styles.srOnly}>. Choose a smaller quantity or a different seller.</span>
            </>
          )}
        </span>
      )}
      {addToCartFailed && (
        <span id={errorId} className={styles.quantityRequirement} role="alert">
          Couldn’t add to cart. Please try again.
        </span>
      )}
    </div>
  );
}

interface SellerOptionRowProps {
  offer: VendorOptionState;
  layout: "table" | "drawer";
  quantity: number;
  /** The first offer in neo's recommended order. */
  isRecommended: boolean;
  /** The offer with the lowest total, shipping included. */
  isLowestPrice: boolean;
  /** The signed-in shopper's purchases from this seller, when they have bought from it before. */
  purchase?: VendorPurchase;
  /** The manufacturer or category requires a license, whatever the vendor says. */
  productRequiresLicense: boolean;
  isAddingToCart: boolean;
  addToCartFailed: boolean;
  /** The offers are being re-priced for a new quantity. */
  isUpdating: boolean;
  onAddToCart: (offer: VendorOptionState) => void;
}
